import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { HeadlineS, LabelM } from 'atoms/typography';
import { Ellipsis } from 'atoms/typography/Ellipsis';
import { fantasy } from 'lib/glossary';

import { AnimatedLineupScoreDifference } from './AnimatedLineupScoreDifference';

const Root = styled(Horizontal)`
  isolation: isolate;
  text-align: left;

  &.revertLayout {
    flex-direction: row-reverse;
    text-align: right;
  }
`;
const PictureWrapper = styled.div`
  width: 40px;
  height: 40px;
`;
const HorizontalLayout = styled(SBHorizontal)`
  width: 100%;
  &.revertLayout {
    flex-direction: row-reverse;
  }
`;
const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  overflow: hidden;
  flex: 1;
`;
const ScoreLabel = styled(LabelM)`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

type Props = {
  managerProfileUrl?: string;
  avatar: ReactNode;
  name: string;
  score: number;
  opponentScore?: number;
  displayExactScore?: boolean;
  isWinner?: boolean;
  greyedOut?: boolean;
  revertLayout?: boolean;
  topRightExtra?: ReactNode;
  className?: string;
};

export const ManagerPoints = ({
  managerProfileUrl,
  avatar,
  name,
  score,
  opponentScore,
  displayExactScore,
  revertLayout,
  topRightExtra,
  className,
}: Props) => {
  return (
    <Root className={classNames(className, { revertLayout })}>
      <PictureWrapper
        as={managerProfileUrl ? Link : 'div'}
        to={managerProfileUrl}
      >
        {avatar}
      </PictureWrapper>
      <Wrapper>
        <HorizontalLayout className={classNames({ revertLayout })}>
          <Horizontal>
            <Ellipsis>
              <LabelM bold as="span">
                {name}
              </LabelM>
            </Ellipsis>
          </Horizontal>
          {topRightExtra || (
            <ScoreLabel color="var(--c-neutral-600)">
              <FormattedMessage
                {...(displayExactScore
                  ? fantasy.pointsWithStyledScore
                  : fantasy.pointsWithStyledOneDecimalScore)}
                values={{
                  score,
                  span: content => (
                    <HeadlineS color="var(--c-neutral-1000)">
                      {content}
                    </HeadlineS>
                  ),
                }}
              />
            </ScoreLabel>
          )}
        </HorizontalLayout>
        <AnimatedLineupScoreDifference
          scoreDifference={score - (opponentScore || 0)}
        />
      </Wrapper>
    </Root>
  );
};
